import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, exportToExcel, Pagination, Typography, useRouteDocumentTitle } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { OutstandingValueCard } from '../components';
import { InvoicesTable } from './components/invoices-table';
import { DateRangeFilter, notSameYear } from '../components/date-range-filter';
import { dateRangeOptions } from '../utils/date-range-options';
import {
  useGetInvoicesReceivablesSummaryQuery,
  useGetInvoicesReceivableDetailsQuery,
  useLazyGetInvoicesExportReceivableDetailsQuery,
} from '@client/api';
import { transformToExcelFormat } from './utils/transformToExcelFormat';
import dayjs from 'dayjs';

export function ReportingRecievedInvoicesRoute() {
  const { t } = useTranslation('reporting');
  const [page, setPage] = useState(0);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dateRangeOptions().currentVsPreviousYear.startDate,
    endDate: dateRangeOptions().currentVsPreviousYear.endDate,
  });
  const [exporting, setExporting] = useState(false);
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.Invoices);

  const { data, isFetching } = useGetInvoicesReceivablesSummaryQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
  });

  const {
    data: invoiceDetailsData,
    isLoading: invoiceDetailsIsLoading,
    isFetching: invoiceDetailsIsFetching,
  } = useGetInvoicesReceivableDetailsQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    page: page,
    pageSize: 10,
  });

  const [trigger] = useLazyGetInvoicesExportReceivableDetailsQuery();
  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await trigger({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess) {
      exportToExcel(
        transformToExcelFormat(
          result.data,
          selectedRows,
          dayjs(timeRange.startDate).year().toString(),
          (dayjs(timeRange.startDate).year() - 1).toString(),
        ),
        'recievable-invoices.xlsx',
      );
    }
    setExporting(false);
  }, [timeRange, trigger, selectedRows]);

  return (
    <ReportPageContent
      breadcrumb={t('navigation.reporting.invoices')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().currentVsPreviousYear, dateRangeOptions().custom]}
          onChange={(dateRange) => {
            setTimeRange({ startDate: dateRange[0], endDate: dateRange[1] });
            setPage(0);
            setSelectedRows(new Set());
          }}
          shouldDisableDate={notSameYear}
          initialStartDate={timeRange.startDate}
          initialEndDate={timeRange.endDate}
          onExportExcel={onExportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <OutstandingValueCard
          isLoading={isFetching}
          title={t('totalOutstanding', { year: dayjs(timeRange.startDate).year() })}
          amount={data?.selectedYearAmount}
        />
        <OutstandingValueCard
          isLoading={isFetching}
          title={t('totalOutstanding', { year: dayjs(timeRange.endDate).year() - 1 })}
          amount={data?.previousYearAmount}
        />
        <OutstandingValueCard
          isLoading={isFetching}
          title={t('development')}
          amount={data?.variance.absolute}
          changeValue={data?.variance.percentage}
          prefix={(data?.variance.absolute.value || 0) > 0 ? '+' : ''}
          showChange
          isInverted
        />
      </div>
      {data?.lastSyncTimestamp && (
        <span className="flex justify-end text-brand-primary text-sm">
          {t('lastUpdated', {
            dateTime: new Date(data?.lastSyncTimestamp).toLocaleString('no-NO'),
          })}
        </span>
      )}
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">
            {t('dateRange', {
              startDate: timeRange.startDate.toLocaleDateString('no-NO'),
              endDate: timeRange.endDate.toLocaleDateString('no-NO'),
            })}
          </Typography>
        </span>
        <Card className="pb-4">
          <InvoicesTable
            data={invoiceDetailsData?.items ?? []}
            isLoading={invoiceDetailsIsFetching || invoiceDetailsIsLoading}
            selectedYear={dayjs(timeRange.startDate).year().toString()}
            comparisonYear={(dayjs(timeRange.startDate).year() - 1).toString()}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
          />
          <Pagination
            totalCount={invoiceDetailsData?.total ?? 0}
            currentPage={page}
            setPage={setPage}
            pageSize={invoiceDetailsData?.pageSize ?? 10}
            visible={true}
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
